import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Header from './components/Header';
import Hero from './components/Hero';
import Features from './components/Features';
import Screenshots from './components/Screenshots';
import Testimonials from './components/Testimonials';
import HowItWorks from './components/HowItWorks';
import Pricing from './components/Pricing';
import Download from './components/Download';
import About from './components/About';
import Footer from './components/Footer';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
import Contact from './pages/Contact';
import Guide from './pages/Guide';
import TeamChatGuide from './pages/TeamChatGuide';
import QuickStartGuide from './pages/QuickStartGuide';
import FirstConversationGuide from './pages/FirstConversationGuide';
import AvailableModelsGuide from './pages/AvailableModelsGuide';
import MultiAIGuide from './pages/MultiAIGuide';
import MCPGuide from './pages/MCPGuide';
import MCPListing from './pages/MCPListing';
import Terms from './pages/Terms';
import Releases from './pages/Releases';
import { ThemeProvider } from './contexts/ThemeContext';

const SHOW_PRICING = false;

// Component to handle scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    // More aggressive scroll to top with multiple fallbacks
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    
    // Additional fallback after a short delay
    setTimeout(() => {
      if (window.pageYOffset !== 0) {
        window.scroll(0, 0);
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
      }
    }, 10);
  }, [pathname]);

  return null;
};

// Home page component
const HomePage = () => (
  <div className="min-h-screen bg-gradient-to-br from-dark-900 via-dark-800 to-secondary-900 dark:from-dark-900 dark:via-dark-800 dark:to-secondary-900 text-white overflow-x-hidden">
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        <Header />
        <Hero />
        <Features />
        <Screenshots />
        <Testimonials />
        <HowItWorks />
        {SHOW_PRICING && <Pricing />}
        <Download />
        <About />
        <Footer />
      </motion.div>
    </AnimatePresence>
  </div>
);

function App() {
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Simulate loading time
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-dark-900 via-dark-800 to-secondary-900 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
            className="w-16 h-16 border-4 border-transparent border-t-secondary-500 border-r-accent-500 rounded-full mx-auto mb-4"
          />
          <h2 className="text-2xl font-display font-bold gradient-text">
            Loading Bervin<span className="loading-dots"></span>
          </h2>
        </motion.div>
      </div>
    );
  }

  return (
    <ThemeProvider>
      {/* Basename removed; site now served at custom domain root. Optional env override: REACT_APP_BASENAME */}
      <Router basename={process.env.REACT_APP_BASENAME && process.env.REACT_APP_BASENAME !== '/' ? process.env.REACT_APP_BASENAME : undefined}>
        <ScrollToTop />
        <div className="min-h-screen bg-dark-900 text-white">
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:id" element={<BlogPost />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/guide" element={<Guide />} />
            <Route path="/guide/team-chat" element={<TeamChatGuide />} />
            <Route path="/guide/quick-start" element={<QuickStartGuide />} />
            <Route path="/guide/first-conversation" element={<FirstConversationGuide />} />
            <Route path="/guide/ai-models" element={<AvailableModelsGuide />} />
            <Route path="/guide/multi-ai" element={<MultiAIGuide />} />
            <Route path="/guide/mcp-setup" element={<MCPGuide />} />
            <Route path="/mcp-servers" element={<MCPListing />} />
            <Route path="/releases" element={<Releases />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Terms />} />
          </Routes>
        </div>
      </Router>
    </ThemeProvider>
  );
}

export default App;
